/**
 * Per-user preferences shared between the API and the frontends.
 */

import { z } from 'zod';

import { currencySchema } from './budget.js';

/**
 * Almanac home-screen layout (see IDEAS.md "Home layout"):
 *  - 'summary' — obligations summary panel + compact section tiles.
 *  - 'tiles'   — large section tiles, no panel.
 */
export const homeLayoutSchema = z.enum(['summary', 'tiles']);
export type HomeLayout = z.infer<typeof homeLayoutSchema>;

/**
 * The AI models offered for document extraction (Documents Phase 3),
 * chosen in Settings. A fixed allowlist keyed by Anthropic model id, so
 * an unknown value can never reach the API. A user's preference is
 * nullable — null/absent means "use `DEFAULT_DOCUMENT_AI_MODEL`", so
 * anyone who never touches it always follows the app default.
 */
export const documentAiModelSchema = z.enum([
  'claude-sonnet-5',
  'claude-opus-4-8',
  'claude-haiku-4-5-20251001',
]);
export type DocumentAiModel = z.infer<typeof documentAiModelSchema>;

export const DEFAULT_DOCUMENT_AI_MODEL: DocumentAiModel = 'claude-sonnet-5';

/** Display metadata for the Settings selector. Order = display order. */
export const documentAiModels: { id: DocumentAiModel; label: string; blurb: string }[] = [
  {
    id: 'claude-sonnet-5',
    label: 'Sonnet 5',
    blurb: 'Balanced accuracy and speed. Recommended for most documents.',
  },
  {
    id: 'claude-opus-4-8',
    label: 'Opus 4.8',
    blurb: 'Most capable — best on faint scans or dense pages. Slower and pricier.',
  },
  {
    id: 'claude-haiku-4-5-20251001',
    label: 'Haiku 4.5',
    blurb: 'Fastest and cheapest. Fine for clean, simple documents.',
  },
];

/**
 * Body for `PATCH /api/shared/me`. Every field is optional so the
 * client can update one preference without resending the rest.
 */
export const meUpdateSchema = z
  .object({
    homeLayout: homeLayoutSchema,
    documentAiModel: documentAiModelSchema,
    // "This is me" — the person record this login corresponds to
    // (Settings → Account). Explicit null clears the link.
    personId: z.string().min(1).max(64).nullable(),
    // Display currency for the Budget page. Explicit null returns to
    // the CHF default.
    currency: currencySchema.nullable(),
  })
  .partial();
export type MeUpdateInput = z.infer<typeof meUpdateSchema>;
